import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import api, { segmentTypesAPI, segmentsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Layers, 
  Info,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Tag,
  Library,
  CheckCircle,
  Briefcase,
  Star,
  Users,
  Eye,
  Heart,
  MessageSquare,
  UserCheck,
  Lightbulb,
  HelpCircle,
  Building2,
  MapPin,
  Globe,
  Target,
  Zap,
  Award,
  Shield,
  Rocket,
  Compass,
  Flag,
  Settings2,
  UserPlus,
  UserMinus,
  Clock,
  LayoutDashboard
} from 'lucide-react';

// Available icons for segment selection
const AVAILABLE_ICONS = [
  { name: 'briefcase', icon: Briefcase, label: 'Briefcase' },
  { name: 'star', icon: Star, label: 'Star' },
  { name: 'users', icon: Users, label: 'Users' },
  { name: 'eye', icon: Eye, label: 'Eye' },
  { name: 'heart', icon: Heart, label: 'Heart' },
  { name: 'message-square', icon: MessageSquare, label: 'Message' },
  { name: 'user-check', icon: UserCheck, label: 'User Check' },
  { name: 'lightbulb', icon: Lightbulb, label: 'Lightbulb' },
  { name: 'help-circle', icon: HelpCircle, label: 'Question' },
  { name: 'building', icon: Building2, label: 'Building' },
  { name: 'map-pin', icon: MapPin, label: 'Location' },
  { name: 'globe', icon: Globe, label: 'Globe' },
  { name: 'target', icon: Target, label: 'Target' },
  { name: 'zap', icon: Zap, label: 'Zap' },
  { name: 'award', icon: Award, label: 'Award' },
  { name: 'shield', icon: Shield, label: 'Shield' },
  { name: 'rocket', icon: Rocket, label: 'Rocket' },
  { name: 'compass', icon: Compass, label: 'Compass' },
  { name: 'flag', icon: Flag, label: 'Flag' },
  { name: 'folder', icon: FolderOpen, label: 'Folder' },
  { name: 'tag', icon: Tag, label: 'Tag' },
];

// Get icon component by name
const getIconByName = (iconName) => {
  const iconDef = AVAILABLE_ICONS.find(i => i.name === iconName);
  return iconDef?.icon || Tag;
};

// Recommended segment types library
const SEGMENT_TYPE_LIBRARY = [
  {
    name: 'Organization',
    description: 'The company or organization a stakeholder is associated with',
    emoji: '🏢',
    icon: 'building'
  },
  {
    name: 'Project Team',
    description: 'A working group whose members could span multiple organizations',
    emoji: '👥',
    icon: 'users'
  },
  {
    name: 'Location',
    description: 'A list of locations where stakeholders work or live',
    emoji: '📍',
    icon: 'map-pin'
  },
  {
    name: 'Involvement',
    description: 'A classification of the level of involvement a stakeholder has in the project',
    emoji: '🎯',
    icon: 'target',
    defaultSegments: [
      { name: 'Team Member', description: 'I am actively working on the project', icon: 'briefcase' },
      { name: 'Stakeholder', description: 'I am not working on the project every day, but I am involved or impacted by the project in some way', icon: 'users' },
      { name: 'Influencer', description: 'I see myself as someone who needs to be engaged for this project to succeed', icon: 'star' },
      { name: 'Community Member', description: 'I live or work around here, and want to ask a question or voice a concern', icon: 'globe' },
      { name: 'Concerned Observer', description: 'I have seen or heard something I would like to discuss or share my feelings', icon: 'eye' },
      { name: 'Innovator', description: 'I have an idea that I\'d like to share', icon: 'lightbulb' },
      { name: 'Just Curious', description: 'I\'d just like to know a bit more about what\'s going on', icon: 'help-circle' }
    ]
  }
];

// Segment Permissions Dialog
function SegmentPermissionsDialog({ segment, allSegments, dashboardPages, onSave, open, onOpenChange }) {
  const [visibleSegmentIds, setVisibleSegmentIds] = useState(null);
  const [visibleDashboardPages, setVisibleDashboardPages] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Initialize state when segment changes
  useEffect(() => {
    if (segment) {
      setVisibleSegmentIds(segment.visible_segment_ids || null);
      setVisibleDashboardPages(segment.visible_dashboard_pages || null);
    }
  }, [segment]);
  
  const isAllSegmentsSelected = visibleSegmentIds === null;
  const isAllPagesSelected = visibleDashboardPages === null;
  
  const toggleAllSegments = () => {
    if (isAllSegmentsSelected) {
      // Deselect all -> empty array
      setVisibleSegmentIds([]);
    } else {
      // Select all -> null
      setVisibleSegmentIds(null);
    }
  };
  
  const toggleSegment = (segId) => {
    if (isAllSegmentsSelected) {
      // Was all selected, now select only others except this one
      const otherIds = allSegments.filter(s => s.id !== segId).map(s => s.id);
      setVisibleSegmentIds(otherIds);
    } else if (visibleSegmentIds.includes(segId)) {
      setVisibleSegmentIds(visibleSegmentIds.filter(id => id !== segId));
    } else {
      setVisibleSegmentIds([...visibleSegmentIds, segId]);
    }
  };
  
  const toggleAllPages = () => {
    if (isAllPagesSelected) {
      setVisibleDashboardPages([]);
    } else {
      setVisibleDashboardPages(null);
    }
  };
  
  const togglePage = (pageId) => {
    if (isAllPagesSelected) {
      const otherIds = dashboardPages.filter(p => p.id !== pageId).map(p => p.id);
      setVisibleDashboardPages(otherIds);
    } else if (visibleDashboardPages.includes(pageId)) {
      setVisibleDashboardPages(visibleDashboardPages.filter(id => id !== pageId));
    } else {
      setVisibleDashboardPages([...visibleDashboardPages, pageId]);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(segment.id, {
        visible_segment_ids: visibleSegmentIds,
        visible_dashboard_pages: visibleDashboardPages
      });
      toast.success('Permissions updated');
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };
  
  // Group segments by type for display (include self so segment can see itself)
  const segmentsByType = useMemo(() => {
    const grouped = {};
    allSegments.forEach(seg => {
      const typeId = seg.segment_type_id;
      if (!grouped[typeId]) {
        grouped[typeId] = { type_name: seg.segment_type_name || typeId, segments: [] };
      }
      grouped[typeId].segments.push(seg);
    });
    return Object.values(grouped);
  }, [allSegments, segment]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-[#5AA2AE]" />
            Permissions: {segment?.name}
          </DialogTitle>
          <DialogDescription>
            Configure what stakeholders in this segment can see across the application.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-4 space-y-6">
          {/* Segment Visibility */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-slate-500" />
                <h4 className="font-semibold text-slate-900">Visible Segments</h4>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleAllSegments}
                className="text-xs"
              >
                {isAllSegmentsSelected ? 'Customize' : 'Allow All'}
              </Button>
            </div>
            <p className="text-sm text-slate-500 mb-3">
              Which segments can stakeholders in "{segment?.name}" see in dashboards and analysis?
            </p>
            
            {isAllSegmentsSelected ? (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                All segments are visible (default)
              </div>
            ) : (
              <ScrollArea className="h-48 border rounded-lg p-2">
                {segmentsByType.map(group => (
                  <div key={group.type_name} className="mb-3">
                    <p className="text-xs font-medium text-slate-500 uppercase mb-1">{group.type_name}</p>
                    <div className="space-y-1">
                      {group.segments.map(seg => (
                        <label 
                          key={seg.id} 
                          className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                        >
                          <Checkbox
                            checked={visibleSegmentIds?.includes(seg.id)}
                            onCheckedChange={() => toggleSegment(seg.id)}
                          />
                          <span className="text-sm">{seg.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </ScrollArea>
            )}
          </div>
          
          <Separator />
          
          {/* Dashboard Page Visibility */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <LayoutDashboard className="w-4 h-4 text-slate-500" />
                <h4 className="font-semibold text-slate-900">Visible Dashboard Pages</h4>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleAllPages}
                className="text-xs"
              >
                {isAllPagesSelected ? 'Customize' : 'Allow All'}
              </Button>
            </div>
            <p className="text-sm text-slate-500 mb-3">
              Which dashboard pages can stakeholders in "{segment?.name}" access?
            </p>
            
            {isAllPagesSelected ? (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                All dashboard pages are visible (default)
              </div>
            ) : (
              <div className="border rounded-lg p-2 space-y-1">
                {dashboardPages.map(page => (
                  <label 
                    key={page.id} 
                    className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                  >
                    <Checkbox
                      checked={visibleDashboardPages?.includes(page.id)}
                      onCheckedChange={() => togglePage(page.id)}
                    />
                    <span className="text-sm">{page.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SegmentsPage() {
  const { projectId } = useParams();
  const { currentProject, segmentTypes, segments, selectProject, refreshAllSegmentData } = useProject();
  const [loading, setLoading] = useState(true);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [segmentDialogOpen, setSegmentDialogOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editingSegment, setEditingSegment] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState({});
  const [typeFormData, setTypeFormData] = useState({ name: '', description: '', icon: '' });
  const [segmentFormData, setSegmentFormData] = useState({ name: '', description: '', segment_type_id: '', icon: '' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedLibraryTypes, setSelectedLibraryTypes] = useState([]);
  const [addingFromLibrary, setAddingFromLibrary] = useState(false);
  
  // Segment stats and permissions
  const [segmentStats, setSegmentStats] = useState({});
  const [dashboardPages, setDashboardPages] = useState([]);
  const [permissionsSegment, setPermissionsSegment] = useState(null);
  const [allSegmentsFlat, setAllSegmentsFlat] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await selectProject(projectId);
      }
      setLoading(false);
    };
    load();
  }, [projectId, currentProject, selectProject]);

  // Fetch segment stats and dashboard pages
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, pagesRes] = await Promise.all([
          api.get(`/projects/${projectId}/segments/stats`),
          api.get(`/projects/${projectId}/dashboard-pages`)
        ]);
        
        // Convert array to map for easy lookup
        const statsMap = {};
        statsRes.data.forEach(s => {
          statsMap[s.id] = s;
        });
        setSegmentStats(statsMap);
        setDashboardPages(pagesRes.data);
      } catch (err) {
        console.error('Failed to fetch segment stats:', err);
      }
    };
    
    if (projectId) {
      fetchStats();
    }
  }, [projectId, segments]);
  
  // Build flat segments list with type names for permissions dialog
  useEffect(() => {
    const flat = [];
    segmentTypes.forEach(st => {
      const typeSegments = segments.filter(s => s.segment_type_id === st.id);
      typeSegments.forEach(seg => {
        flat.push({
          ...seg,
          segment_type_name: st.name
        });
      });
    });
    setAllSegmentsFlat(flat);
  }, [segmentTypes, segments]);

  // Auto-expand all types on load
  useEffect(() => {
    if (segmentTypes.length > 0) {
      const expanded = {};
      segmentTypes.forEach(st => {
        expanded[st.id] = true;
      });
      setExpandedTypes(expanded);
    }
  }, [segmentTypes]);

  const toggleExpanded = (typeId) => {
    setExpandedTypes(prev => ({
      ...prev,
      [typeId]: !prev[typeId]
    }));
  };

  const getSegmentsForType = (typeId) => {
    return segments.filter(s => s.segment_type_id === typeId);
  };

  // Segment Type handlers
  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingType) {
        await segmentTypesAPI.update(projectId, editingType.id, typeFormData);
        toast.success('Segment type updated');
      } else {
        await segmentTypesAPI.create(projectId, typeFormData);
        toast.success('Segment type created');
      }
      await refreshAllSegmentData();
      setTypeDialogOpen(false);
      setTypeFormData({ name: '', description: '', icon: '' });
      setEditingType(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save segment type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setTypeFormData({ name: type.name, description: type.description || '', icon: type.icon || '' });
    setTypeDialogOpen(true);
  };

  const handleDeleteType = async (typeId) => {
    const typeSegments = getSegmentsForType(typeId);
    const confirmMsg = typeSegments.length > 0 
      ? `Delete this segment type and its ${typeSegments.length} segment(s)?`
      : 'Delete this segment type?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await segmentTypesAPI.delete(projectId, typeId);
      toast.success('Segment type deleted');
      await refreshAllSegmentData();
    } catch (err) {
      toast.error('Failed to delete segment type');
    }
  };

  const openCreateTypeDialog = () => {
    setEditingType(null);
    setTypeFormData({ name: '', description: '', icon: '' });
    setTypeDialogOpen(true);
  };

  // Segment handlers
  const handleSegmentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingSegment) {
        await segmentsAPI.update(projectId, editingSegment.id, {
          name: segmentFormData.name,
          description: segmentFormData.description,
          icon: segmentFormData.icon || null
        });
        toast.success('Segment updated');
      } else {
        await segmentsAPI.create(projectId, {
          ...segmentFormData,
          icon: segmentFormData.icon || null
        });
        toast.success('Segment created');
      }
      await refreshAllSegmentData();
      setSegmentDialogOpen(false);
      setSegmentFormData({ name: '', description: '', segment_type_id: '', icon: '' });
      setEditingSegment(null);
      setSelectedTypeId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save segment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSegment = (segment) => {
    setEditingSegment(segment);
    setSegmentFormData({ 
      name: segment.name, 
      description: segment.description || '',
      segment_type_id: segment.segment_type_id,
      icon: segment.icon || ''
    });
    setSegmentDialogOpen(true);
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm('Delete this segment?')) return;
    
    try {
      await segmentsAPI.delete(projectId, segmentId);
      toast.success('Segment deleted');
      await refreshAllSegmentData();
    } catch (err) {
      toast.error('Failed to delete segment');
    }
  };

  const openCreateSegmentDialog = (typeId) => {
    setEditingSegment(null);
    setSelectedTypeId(typeId);
    setSegmentFormData({ name: '', description: '', segment_type_id: typeId, icon: '' });
    setSegmentDialogOpen(true);
  };
  
  // Save segment permissions
  const handleSavePermissions = async (segmentId, permissions) => {
    await segmentsAPI.update(projectId, segmentId, permissions);
    await refreshAllSegmentData();
  };

  // Library handlers
  const toggleLibraryType = (typeName) => {
    setSelectedLibraryTypes(prev => 
      prev.includes(typeName)
        ? prev.filter(n => n !== typeName)
        : [...prev, typeName]
    );
  };

  const handleAddFromLibrary = async () => {
    if (selectedLibraryTypes.length === 0) {
      toast.error('Please select at least one segment type');
      return;
    }

    setAddingFromLibrary(true);
    
    try {
      let totalSegmentsAdded = 0;
      
      for (const typeName of selectedLibraryTypes) {
        const libraryType = SEGMENT_TYPE_LIBRARY.find(t => t.name === typeName);
        if (libraryType) {
          const response = await segmentTypesAPI.create(projectId, {
            name: libraryType.name,
            description: libraryType.description,
            icon: libraryType.icon || null
          });
          const newType = response.data;
          
          if (libraryType.defaultSegments && libraryType.defaultSegments.length > 0) {
            for (const defaultSeg of libraryType.defaultSegments) {
              await segmentsAPI.create(projectId, {
                name: defaultSeg.name,
                description: defaultSeg.description,
                segment_type_id: newType.id,
                icon: defaultSeg.icon || null
              });
              totalSegmentsAdded++;
            }
          }
        }
      }
      
      const message = totalSegmentsAdded > 0 
        ? `Added ${selectedLibraryTypes.length} segment type(s) with ${totalSegmentsAdded} default segments`
        : `Added ${selectedLibraryTypes.length} segment type(s)`;
      toast.success(message);
      await refreshAllSegmentData();
      setLibraryOpen(false);
      setSelectedLibraryTypes([]);
    } catch (err) {
      toast.error('Failed to add segment types from library');
    } finally {
      setAddingFromLibrary(false);
    }
  };

  // Check which library types already exist
  const existingTypeNames = segmentTypes.map(t => t.name.toLowerCase());
  const isTypeExists = (name) => existingTypeNames.includes(name.toLowerCase());

  const typeColors = [
    { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', headerBg: 'bg-blue-50' },
    { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', headerBg: 'bg-emerald-50' },
    { bg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200', headerBg: 'bg-violet-50' },
    { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', headerBg: 'bg-amber-50' },
    { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200', headerBg: 'bg-rose-50' },
    { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200', headerBg: 'bg-cyan-50' },
  ];

  const getTypeColor = (index) => typeColors[index % typeColors.length];

  // Helper to render permission badges
  const renderPermissionBadge = (segment) => {
    const hasSegmentRestrictions = segment.visible_segment_ids !== null && segment.visible_segment_ids !== undefined;
    const hasPageRestrictions = segment.visible_dashboard_pages !== null && segment.visible_dashboard_pages !== undefined;
    
    if (!hasSegmentRestrictions && !hasPageRestrictions) {
      return (
        <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200 bg-emerald-50">
          Full Access
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline" className="text-xs text-amber-600 border-amber-200 bg-amber-50">
        Restricted
      </Badge>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Segments
          </h1>
          <p className="text-slate-600 mt-1">
            Organize stakeholders and configure visibility permissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Library Button */}
          <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                onClick={() => setSelectedLibraryTypes([])}
                data-testid="segment-type-library-btn"
              >
                <Library className="mr-2 h-4 w-4" />
                Library
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" aria-describedby="library-description">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Segment Type Library
                </DialogTitle>
                <DialogDescription id="library-description">
                  Select recommended segment types to add to your project
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
                <div className="space-y-3">
                  {SEGMENT_TYPE_LIBRARY.map((segType) => {
                    const alreadyExists = isTypeExists(segType.name);
                    const isSelected = selectedLibraryTypes.includes(segType.name);
                    const hasDefaultSegments = segType.defaultSegments && segType.defaultSegments.length > 0;
                    
                    return (
                      <div
                        key={segType.name}
                        className={`relative flex items-start gap-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          alreadyExists 
                            ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'
                            : isSelected
                              ? 'border-[#5AA2AE] bg-[#5AA2AE]/5'
                              : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => !alreadyExists && toggleLibraryType(segType.name)}
                        data-testid={`library-segment-type-${segType.name.toLowerCase().replace(' ', '-')}`}
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-lg">
                          {segType.emoji}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-slate-900">{segType.name}</h4>
                            {alreadyExists && (
                              <span className="inline-flex items-center text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Added
                              </span>
                            )}
                            {hasDefaultSegments && !alreadyExists && (
                              <span className="inline-flex items-center text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                                +{segType.defaultSegments.length} segments
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-0.5">{segType.description}</p>
                          {hasDefaultSegments && !alreadyExists && (
                            <p className="text-xs text-slate-400 mt-1">
                              Includes: {segType.defaultSegments.slice(0, 3).map(s => s.name).join(', ')}
                              {segType.defaultSegments.length > 3 && ` +${segType.defaultSegments.length - 3} more`}
                            </p>
                          )}
                        </div>
                        {!alreadyExists && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleLibraryType(segType.name)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-slate-500">
                  {selectedLibraryTypes.length} selected
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setLibraryOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    onClick={handleAddFromLibrary}
                    disabled={selectedLibraryTypes.length === 0 || addingFromLibrary}
                    data-testid="add-segment-types-from-library-btn"
                  >
                    {addingFromLibrary ? 'Adding...' : `Add ${selectedLibraryTypes.length || ''} Type${selectedLibraryTypes.length !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Segment Type Button */}
          <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                onClick={openCreateTypeDialog}
                data-testid="create-segment-type-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Segment Type
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingType ? 'Edit Segment Type' : 'Create Segment Type'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleTypeSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="type-name">Type Name</Label>
                  <Input
                    id="type-name"
                    placeholder="e.g., Organisation, Involvement, Project Team"
                    value={typeFormData.name}
                    onChange={(e) => setTypeFormData({ ...typeFormData, name: e.target.value })}
                    required
                    data-testid="segment-type-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type-description">Description (optional)</Label>
                  <Textarea
                    id="type-description"
                    placeholder="What does this segment type represent?"
                    value={typeFormData.description}
                    onChange={(e) => setTypeFormData({ ...typeFormData, description: e.target.value })}
                    rows={3}
                    data-testid="segment-type-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default Icon (optional)</Label>
                  <p className="text-xs text-slate-500 mb-2">Segments in this type will use this icon unless they have their own</p>
                  <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-slate-50">
                    {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setTypeFormData({ ...typeFormData, icon: typeFormData.icon === name ? '' : name })}
                        className={`p-2 rounded-lg transition-all ${
                          typeFormData.icon === name 
                            ? 'bg-[#5AA2AE] text-white ring-2 ring-[#5AA2AE]/30' 
                            : 'bg-white hover:bg-slate-100 text-slate-600'
                        }`}
                        title={label}
                      >
                        <Icon className="h-5 w-5" />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setTypeDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    disabled={submitting}
                    data-testid="segment-type-submit-btn"
                  >
                    {submitting ? 'Saving...' : (editingType ? 'Update' : 'Create')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-[#5AA2AE]/5 border-[#5AA2AE]/20">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-[#5AA2AE] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            <strong>Segment Types & Segments:</strong> First create Segment Types (e.g., "Organisation", "Role", "Location"). 
            Then add specific Segments under each type. Configure permissions to control what each segment can see in dashboards.
          </div>
        </CardContent>
      </Card>

      {/* Segment Types List */}
      {loading ? (
        <div className="space-y-4">
          {Array(2).fill(0).map((_, i) => (
            <Card key={i} className="dashboard-card">
              <Skeleton className="h-40" />
            </Card>
          ))}
        </div>
      ) : segmentTypes.length === 0 ? (
        <Card className="dashboard-card">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Layers className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No segment types yet</h3>
            <p className="text-slate-500 mb-4">Create segment types to organize your stakeholders</p>
            <div className="flex items-center justify-center gap-3">
              <Button 
                variant="outline"
                onClick={() => setLibraryOpen(true)}
              >
                <Library className="mr-2 h-4 w-4" />
                Browse Library
              </Button>
              <Button 
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                onClick={openCreateTypeDialog}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Custom Type
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {segmentTypes.map((type, index) => {
            const color = getTypeColor(index);
            const typeSegments = getSegmentsForType(type.id);
            const isExpanded = expandedTypes[type.id];
            const TypeIcon = type.icon ? getIconByName(type.icon) : FolderOpen;

            return (
              <Card 
                key={type.id} 
                className={`overflow-hidden ${color.border}`}
                data-testid={`segment-type-card-${type.id}`}
              >
                {/* Header */}
                <div className={`${color.headerBg} px-4 py-3 border-b ${color.border}`}>
                  <div className="flex items-center justify-between">
                    <button 
                      className="flex items-center gap-3 text-left hover:opacity-80 transition-opacity"
                      onClick={() => toggleExpanded(type.id)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      )}
                      <div className={`w-9 h-9 rounded-lg ${color.bg} flex items-center justify-center`}>
                        <TypeIcon className={`w-5 h-5 ${color.text}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {type.name}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {typeSegments.length} segment{typeSegments.length !== 1 ? 's' : ''}
                          {type.description && ` · ${type.description}`}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openCreateSegmentDialog(type.id)}
                        data-testid={`add-segment-to-${type.id}`}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Segment
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditType(type)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit Type
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => handleDeleteType(type.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Type
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>

                {/* Table Content */}
                {isExpanded && (
                  <div className="p-0">
                    {typeSegments.length === 0 ? (
                      <div className="text-center py-8 bg-slate-50">
                        <Tag className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No segments in this type yet</p>
                        <Button
                          size="sm"
                          variant="link"
                          className="mt-2"
                          onClick={() => openCreateSegmentDialog(type.id)}
                        >
                          Add first segment
                        </Button>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b">
                            <tr>
                              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">
                                Segment
                              </th>
                              <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 w-24">
                                <div className="flex items-center justify-center gap-1">
                                  <UserPlus className="w-3.5 h-3.5" />
                                  Invited
                                </div>
                              </th>
                              <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 w-24">
                                <div className="flex items-center justify-center gap-1">
                                  <UserMinus className="w-3.5 h-3.5" />
                                  Ref
                                </div>
                              </th>
                              <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 w-24">
                                <div className="flex items-center justify-center gap-1">
                                  <Clock className="w-3.5 h-3.5" />
                                  Pending
                                </div>
                              </th>
                              <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 w-36">
                                Permissions
                              </th>
                              <th className="w-12"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {typeSegments.map((segment) => {
                              const segmentIconName = segment.icon || type.icon;
                              const SegmentIcon = segmentIconName ? getIconByName(segmentIconName) : Tag;
                              const stats = segmentStats[segment.id] || { invited: 0, reference: 0, pending: 0 };
                              
                              return (
                                <tr 
                                  key={segment.id} 
                                  className="hover:bg-slate-50/50 transition-colors"
                                  data-testid={`segment-row-${segment.id}`}
                                >
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-md ${color.bg} flex items-center justify-center flex-shrink-0`}>
                                        <SegmentIcon className={`w-4 h-4 ${color.text}`} />
                                      </div>
                                      <div className="min-w-0">
                                        <p className="font-medium text-slate-900">{segment.name}</p>
                                        {segment.description && (
                                          <p className="text-xs text-slate-500 truncate max-w-xs">{segment.description}</p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <span className="text-sm font-medium text-emerald-600">{stats.invited}</span>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <span className="text-sm font-medium text-violet-600">{stats.reference}</span>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <span className="text-sm font-medium text-amber-600">{stats.pending}</span>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2"
                                      onClick={() => setPermissionsSegment(segment)}
                                      data-testid={`permissions-btn-${segment.id}`}
                                    >
                                      {renderPermissionBadge(segment)}
                                    </Button>
                                  </td>
                                  <td className="px-2 py-3">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          className="h-8 w-8"
                                        >
                                          <MoreVertical className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => handleEditSegment(segment)}>
                                          <Pencil className="mr-2 h-4 w-4" />
                                          Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setPermissionsSegment(segment)}>
                                          <Settings2 className="mr-2 h-4 w-4" />
                                          Permissions
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                          className="text-red-600"
                                          onClick={() => handleDeleteSegment(segment.id)}
                                        >
                                          <Trash2 className="mr-2 h-4 w-4" />
                                          Delete
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Segment Dialog */}
      <Dialog open={segmentDialogOpen} onOpenChange={setSegmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingSegment ? 'Edit Segment' : 'Add Segment'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSegmentSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="segment-name">Segment Name</Label>
              <Input
                id="segment-name"
                placeholder="e.g., Finance, Engineering, Project Lead"
                value={segmentFormData.name}
                onChange={(e) => setSegmentFormData({ ...segmentFormData, name: e.target.value })}
                required
                data-testid="segment-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="segment-description">Description (optional)</Label>
              <Textarea
                id="segment-description"
                placeholder="What does this segment represent?"
                value={segmentFormData.description}
                onChange={(e) => setSegmentFormData({ ...segmentFormData, description: e.target.value })}
                rows={2}
                data-testid="segment-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Icon (optional)</Label>
              <p className="text-xs text-slate-500 mb-2">Leave empty to use the segment type's default icon</p>
              <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-slate-50">
                {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setSegmentFormData({ ...segmentFormData, icon: segmentFormData.icon === name ? '' : name })}
                    className={`p-2 rounded-lg transition-all ${
                      segmentFormData.icon === name 
                        ? 'bg-[#5AA2AE] text-white ring-2 ring-[#5AA2AE]/30' 
                        : 'bg-white hover:bg-slate-100 text-slate-600'
                    }`}
                    title={label}
                  >
                    <Icon className="h-5 w-5" />
                  </button>
                ))}
              </div>
            </div>
            {selectedTypeId && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  Adding to: <strong>{segmentTypes.find(t => t.id === selectedTypeId)?.name}</strong>
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setSegmentDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                disabled={submitting}
                data-testid="segment-submit-btn"
              >
                {submitting ? 'Saving...' : (editingSegment ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Permissions Dialog */}
      <SegmentPermissionsDialog
        segment={permissionsSegment}
        allSegments={allSegmentsFlat}
        dashboardPages={dashboardPages}
        onSave={handleSavePermissions}
        open={!!permissionsSegment}
        onOpenChange={(open) => !open && setPermissionsSegment(null)}
      />
    </div>
  );
}
